import { useState, useEffect } from 'react';
import API_CONFIG from '../../config/api';
import './EditAdminModal.css'; // Reusing styles

function EditUserModal({ isOpen, onClose, onSuccess, user, onImpersonate, adminData }) {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        kycStatus: 'pending',
        isActive: true,
        isRestricted: false,
        creditScore: '',
        availableCredit: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const adminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;

    useEffect(() => {
        if (user) {
            // Fetch fresh data for detailed edit
            fetchUserDetails(user.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const fetchUserDetails = async (id) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${id}`, {
                headers: { 'x-user-id': adminId }
            });
            const data = await res.json();
            if (data.success) {
                const u = data.data.user;
                setFormData({
                    firstName: u.first_name || '',
                    lastName: u.last_name || '',
                    email: u.email || '',
                    phone: u.phone || '',
                    kycStatus: u.kyc_status || 'pending',
                    isActive: u.is_active || false,
                    isRestricted: u.is_restricted || false,
                    creditScore: u.credit_score || '',
                    availableCredit: u.available_credit || ''
                });
            }
        } catch (err) {
            console.error('Error fetching user:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminId
                },
                body: JSON.stringify({
                    ...formData,
                    is_active: formData.isActive,
                    kyc_status: formData.kycStatus,
                    credit_score: formData.creditScore,
                    available_credit: formData.availableCredit
                })
            });

            const data = await res.json();
            if (data.success) {
                onSuccess();
                onClose();
            } else {
                setError(data.message || 'Update failed');
            }
        } catch (err) {
            setError('Server connection error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForcePasswordReset = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.id}/expire-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminId
                }
            });
            const data = await res.json();
            if (data.success) {
                alert('Password marked for reset. User must set a new password on next login.');
            } else {
                alert(data.message || 'Failed to force password reset.');
            }
        } catch (err) {
            console.error('Force reset error:', err);
            alert('Network error while forcing reset.');
        }
    };

    const handleToggleRestriction = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.id}/restrict`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminId
                },
                body: JSON.stringify({ restricted: !formData.isRestricted })
            });
            const data = await res.json();
            if (data.success) {
                await fetchUserDetails(user.id);
                alert(!formData.isRestricted ? 'User restricted (login blocked).' : 'User unrestricted.');
            } else {
                alert(data.message || 'Failed to update restriction.');
            }
        } catch (err) {
            console.error('Restrict user error:', err);
            alert('Network error while updating restriction.');
        }
    };

    const canResetPassword = adminData?.role === 'super_admin' || adminData?.permissions?.includes('users.reset_password');

    const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);

    const passwordStrength = (() => {
        const pwd = newPassword || '';
        let score = 0;
        if (pwd.length >= 8) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[!@#$%^&*]/.test(pwd)) score++;
        const levels = ['Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
        const colors = ['#ef4444', '#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#10b981'];
        return { label: levels[score], color: colors[score], score };
    })();

    const handleSetPassword = async () => {
        if (!newPassword || newPassword.length < 8) {
            alert('Password must be at least 8 characters.');
            return;
        }
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.id}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminId
                },
                body: JSON.stringify({ newPassword })
            });
            const data = await res.json();
            if (data.success) {
                alert('Password updated successfully.');
                setShowSetPasswordModal(false);
                setNewPassword('');
            } else {
                alert(data.message || 'Failed to reset password.');
            }
        } catch (err) {
            console.error('Reset password error:', err);
            alert('Network error while resetting password.');
        }
    };

    if (!isOpen) return null;

    return (
        <>
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content edit-admin-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>👤 Edit User: {user?.name}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {error && <div className="modal-error">{error}</div>}

                <form onSubmit={handleSubmit} className="edit-form">
                    <div className="form-section">
                        <h3>📋 Personal Information</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Phone Number</label>
                            <input
                                type="text"
                                value={formData.phone}
                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>🛡️ Account & Compliance</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>KYC Status</label>
                                <select
                                    value={formData.kycStatus}
                                    onChange={e => setFormData({ ...formData, kycStatus: e.target.value })}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Verified</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Credit Score</label>
                                <input
                                    type="number"
                                    value={formData.creditScore}
                                    onChange={e => setFormData({ ...formData, creditScore: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Available Credit (₹)</label>
                            <input
                                type="number"
                                value={formData.availableCredit}
                                onChange={e => setFormData({ ...formData, availableCredit: e.target.value })}
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                    Active Account
                                </label>
                            </div>
                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.isRestricted}
                                        onChange={e => setFormData({ ...formData, isRestricted: e.target.checked })}
                                    />
                                    Restrict Login
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-section security-section">
                        <h3>🛡️ Admin Controls</h3>
                        <div className="security-notice">
                            <p>Quick actions for super/admin users to control client access.</p>
                            <div className="action-chip-row">
                                <button
                                    type="button"
                                    className="btn btn-login-shortcut"
                                    onClick={handleForcePasswordReset}
                                >
                                    Force Password Reset
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-login-shortcut ghost"
                                    onClick={handleToggleRestriction}
                                >
                                    {formData.isRestricted ? 'Unrestrict User' : 'Restrict User'}
                                </button>
                                {canResetPassword && (
                                    <button
                                        type="button"
                                        className="btn btn-login-shortcut"
                                        onClick={() => {
                                            setShowSetPasswordModal(true);
                                            setNewPassword('');
                                        }}
                                    >
                                        Set New Password
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={isLoading}>
                            {isLoading ? 'Updating...' : 'Save User Data'}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        {showSetPasswordModal && (
            <div className="modal-overlay" onClick={() => setShowSetPasswordModal(false)}>
                <div className="modal-content small-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>Set New Password</h3>
                        <button className="modal-close-btn" onClick={() => setShowSetPasswordModal(false)}>×</button>
                    </div>
                    <div className="modal-body">
                        <div className="mini-card">
                            <div className="mini-head">
                                <div className="avatar-ring small">{(user?.name || 'U')[0]}</div>
                                <div>
                                    <div className="mini-title">{user?.name}</div>
                                    <div className="mini-sub">{formData.email}</div>
                                </div>
                            </div>
                            <div className="mini-meta">
                                <span>Phone: {formData.phone || 'N/A'}</span>
                                <span>Status: {formData.isActive ? 'Active' : 'Inactive'}</span>
                                <span>KYC: {formData.kycStatus}</span>
                            </div>
                        </div>
                            <div className="form-group">
                                <label>New Password (min 8 chars)</label>
                                <div className="password-field">
                                    <div className="input-with-toggle">
                                        <input
                                            type={showNewPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                        />
                                        <button
                                            type="button"
                                            className="ghost-toggle"
                                            onClick={() => setShowNewPassword(v => !v)}
                                        >
                                            {showNewPassword ? 'Hide' : 'Show'}
                                        </button>
                                    </div>
                                    <div className="strength-bar">
                                        {[0,1,2,3,4].map(i => (
                                            <span
                                                key={i}
                                                className={`strength-seg ${passwordStrength.score > i ? 'filled' : ''}`}
                                                style={passwordStrength.score > i ? { background: passwordStrength.color } : {}}
                                            />
                                        ))}
                                    </div>
                                    <div className="strength-label" style={{ color: passwordStrength.color }}>
                                        {passwordStrength.label}
                                    </div>
                                    <span className="hint">Upper, lower, number & special required.</span>
                                </div>
                            </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn-secondary" onClick={() => setShowSetPasswordModal(false)}>Cancel</button>
                        <button className="btn-primary" onClick={handleSetPassword}>Save Password</button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

export default EditUserModal;
